import { useCallback, useEffect } from "react";
import { useSearchParams } from "react-router-dom";

/**
 * Wybrany podmiot trzymamy w adresie, nie w stanie aplikacji.
 *
 * Dzięki temu odsyłacz do listy projektów spółki jest odsyłaczem, a nie
 * instrukcją „przełącz się i kliknij". Przy dwóch spółkach ludzie wysyłają
 * sobie takie odsyłacze bez przerwy.
 *
 * Sam adres jednak nie wystarczy: przejście do portfela albo do produkcji
 * idzie przez ``<Link to="/portfel">``, który parametru nie niesie — wybór
 * znikał więc przy pierwszym kliknięciu w menu i wyglądało to na niedziałający
 * filtr. Dlatego wybór jest **zapamiętywany na czas sesji karty** i
 * przywracany do adresu, gdy ten o podmiocie milczy (``useOrganizationSync``).
 */

const KLUCZ = "podmiot";

/** Zapamiętany wybór. ``null`` = nikt jeszcze nie wybierał, ``""`` = wszystkie. */
function zapamietany(): string | null {
  try {
    return sessionStorage.getItem(KLUCZ);
  } catch {
    return null;
  }
}

function zapamietaj(code: string) {
  try {
    sessionStorage.setItem(KLUCZ, code);
  } catch {
    /* Tryb prywatny potrafi rzucić — wybór działa wtedy w obrębie adresu. */
  }
}

export function useOrganization() {
  const [params, setParams] = useSearchParams();
  const organization = params.get(KLUCZ);

  const setOrganization = useCallback(
    (code: string | null) => {
      const kopia = new URLSearchParams(params);
      if (code) kopia.set(KLUCZ, code);
      else kopia.delete(KLUCZ);
      // Pusty ciąg to świadome „wszystkie podmioty" i musi być zapamiętany
      // tak samo jak wybór spółki — inaczej przywracanie z sesji cofałoby
      // decyzję użytkownika przy następnym przejściu.
      zapamietaj(code ?? "");
      setParams(kopia, { replace: true });
    },
    [params, setParams],
  );

  return { organization, setOrganization };
}

/**
 * Przywraca wybór podmiotu do adresu po nawigacji, która go zgubiła.
 *
 * Wołane **raz**, w powłoce aplikacji: gdyby robił to sam ``useOrganization``,
 * kilka komponentów naraz przepisywałoby ten sam adres.
 */
export function useOrganizationSync() {
  const [params, setParams] = useSearchParams();
  const wAdresie = params.get(KLUCZ);

  useEffect(() => {
    if (wAdresie) {
      zapamietaj(wAdresie);
      return;
    }
    const zapisany = zapamietany();
    if (!zapisany) return;
    const kopia = new URLSearchParams(params);
    kopia.set(KLUCZ, zapisany);
    setParams(kopia, { replace: true });
  }, [wAdresie, params, setParams]);
}
